/**
 * PooBucksBalanceChip — compact read-only balance chip for the customer
 * dashboard.
 *
 * Data source: `useAuth().profile.credit_balance` — the SAME denormalized
 * `credit_balance` float on the customer_profile document that
 * `CustomerBillingPage` reads (both surfaces stay pinned to the profile
 * fetch, no separate `/credits/me` round-trip needed for the balance
 * value itself \u2014 the full ledger history lives on the billing page).
 *
 * Rendering rules (locked by tests in `PooBucksBalanceChip.test.jsx`):
 *   \u2022 Positive balance   \u2192 `$X.XX` shown in the accent orange.
 *   \u2022 Zero balance       \u2192 `$0.00` shown neutrally in the muted-text
 *                              color. NO "get more" upsell, NO "you're
 *                              missing out" shame framing, NO fill-me-up
 *                              CTA. Just the neutral number.
 *   \u2022 No redemption UX   \u2192 NO "redeem" button, NO "apply to booking"
 *                              button, NO "learn more" that hints at
 *                              future redemption flow. All redemption
 *                              intent is deferred to the Stripe phase
 *                              (see grep marker `TODO (Stripe phase):
 *                              apply credit to booking/payout`).
 *   \u2022 "View history" is permitted \u2014 read-only ledger drilldown, not
 *      a redemption surface.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";

export default function PooBucksBalanceChip() {
  const { profile } = useAuth();
  const { creditLabel } = useConfig();
  const balance = Number(profile?.credit_balance || 0);
  const isZero = balance === 0;

  return (
    <div
      className="inline-flex items-center gap-3 rounded-full border border-pp-border bg-pp-surface2 px-5 py-2"
      data-testid="poobucks-balance-chip"
      style={{ boxShadow: "var(--pp-card-shadow)" }}
    >
      <span
        className="text-[11px] font-bold uppercase tracking-[0.10em] text-pp-text-muted"
        data-testid="poobucks-balance-label"
      >
        {creditLabel}
      </span>
      <span
        className={
          "font-heading text-lg font-bold tabular-nums " +
          (isZero ? "text-pp-text-muted" : "text-brand-orange")
        }
        data-testid="poobucks-balance-value"
      >
        ${balance.toFixed(2)}
      </span>
      <Link
        to="/dashboard/customer/billing"
        className="text-[11px] font-semibold text-pp-text-muted underline-offset-2 hover:underline"
        data-testid="poobucks-balance-history-link"
      >
        View history
      </Link>
    </div>
  );
}
